import { useEffect, useMemo, useRef, useState } from "react";

import { sichtbareSchritte, type FeldWert, type KonfigurationsFeld, type KonfigurationsZustand } from "@repo/shared";

import { FeldEingabe } from "../FeldEingabe";
import { zahlText } from "./FeldRahmen";
import { VorschauBausteine } from "./VorschauBausteine";

/** Anzeigewert eines Feldes für die Zusammenfassung: Optionsname, Zahl mit Einheit, ja/nein. */
function anzeigeWert(feld: KonfigurationsFeld, wert: FeldWert | undefined): string {
  if (wert === undefined || wert === "") return "—";
  if (feld.zustand.anzeige && wert === feld.zustand.wert) return feld.zustand.anzeige;
  switch (feld.art) {
    case "auswahl":
      return feld.optionen.find((o) => o.wert === wert)?.label ?? String(wert);
    case "mass": {
      const zahl = typeof wert === "number" ? wert : Number(wert);
      const text = Number.isFinite(zahl) ? zahlText(zahl) : String(wert);
      return feld.einheit ? `${text} ${feld.einheit}` : text;
    }
    case "schalter":
      return wert === true ? "ja" : "nein";
    default:
      return String(wert);
  }
}

/** Zuletzt beendete Konfigurationsanfrage: ihre Nummer und ob ihre Antwort übernommen wurde. */
export type ErledigteAnfrage = { nummer: number; uebernommen: boolean };

/**
 * Konfigurator als Wizard: Schrittleiste, je Schritt seine Felder und
 * Vorschau-Bausteine, „Zurück“ und „Weiter“. Welche Schritte es gibt, steht
 * in der Antwort des Servers – der Wizard kennt kein Produkt.
 */
export function KonfiguratorWizard({
  stand,
  werte,
  aendern,
  rechnet,
  gestarteteAnfrage,
  erledigteAnfrage,
}: {
  stand: KonfigurationsZustand;
  werte: Record<string, FeldWert>;
  aendern: (schluessel: string, wert: FeldWert) => void;
  rechnet: boolean;
  /** Nummer der zuletzt gestarteten Anfrage, beim Klick gelesen. */
  gestarteteAnfrage: () => number;
  /** Zuletzt beendete Anfrage; kommt im selben Rendervorgang wie ihr Stand. */
  erledigteAnfrage: ErledigteAnfrage | null;
}) {
  const vorschau = useMemo(() => stand.ergebnis?.vorschau ?? [], [stand]);
  const schritte = useMemo(
    () => sichtbareSchritte(stand.schritte ?? [], stand.felder.map((f) => f.schluessel), vorschau),
    [stand, vorschau],
  );
  const felder = useMemo(() => new Map(stand.felder.map((f) => [f.schluessel, f])), [stand]);

  const [aktuell, setAktuell] = useState(() => schritte[0]?.schluessel ?? "");
  const [besucht, setBesucht] = useState(() => new Set(schritte[0] ? [schritte[0].schluessel] : []));
  /** Reihenfolge der zuletzt gezeigten Schritte – nötig, wenn der aktuelle verschwindet. */
  const vorherige = useRef(schritte.map((s) => s.schluessel));
  const titel = useRef<HTMLHeadingElement>(null);
  const navigiert = useRef(false);
  /** Vorgemerkter Sprung nach einer Kartenwahl: Ausgangsschritt und die beim Klick zuletzt gestartete Anfrage. */
  const sprung = useRef<{ von: string; nach: number } | null>(null);

  // Verschwindet der aktuelle Schritt (etwa nach einem Wechsel der Ausführung),
  // gilt der nächste aus der alten Reihenfolge, sonst der letzte.
  const nachfolgerAusAlt = (von: string) => {
    const alt = vorherige.current;
    const nachfolger = alt.slice(alt.indexOf(von) + 1).find((k) => schritte.some((s) => s.schluessel === k));
    return nachfolger ? schritte.findIndex((s) => s.schluessel === nachfolger) : schritte.length - 1;
  };
  let index = schritte.findIndex((s) => s.schluessel === aktuell);
  if (index < 0) index = nachfolgerAusAlt(aktuell);
  const schritt = schritte[index];

  function springeZu(schluessel: string) {
    navigiert.current = true;
    setAktuell(schluessel);
    setBesucht((alt) => new Set(alt).add(schluessel));
  }

  // Ein Kartensprung wartet auf die Antwort des Servers: Die Wahl kann Schritte
  // ein- oder ausblenden, der Sprung gilt also der neuen Liste. Die Wahl kennt erst
  // eine Anfrage, die nach dem Klick gestartet ist – auch die nachgeholte, wenn beim
  // Klick noch eine ältere lief; deren Ende zählt nicht. Wird die Antwort nicht
  // übernommen (Fehler, verworfen), verfällt der Sprung: Der Nutzer bleibt stehen
  // und klickt „Weiter“. Ref statt Effekt-Abhängigkeiten: Der Sprung reagiert nur
  // auf das Ende einer Anfrage, liest aber Schritte und Position dieses Rendervorgangs.
  const sprungAufloesen = useRef<(erledigt: ErledigteAnfrage) => void>(() => {});
  sprungAufloesen.current = (erledigt) => {
    const s = sprung.current;
    if (!s || erledigt.nummer <= s.nach) return;
    sprung.current = null;
    if (!erledigt.uebernommen) return;
    const pos = schritte.findIndex((x) => x.schluessel === s.von);
    const naechster = schritte[pos >= 0 ? pos + 1 : nachfolgerAusAlt(s.von)];
    if (naechster && naechster.schluessel !== aktuell) springeZu(naechster.schluessel);
  };
  // Steht vor dem Effekt unten, damit `vorherige` noch die alte Reihenfolge hält.
  useEffect(() => {
    if (erledigteAnfrage) sprungAufloesen.current(erledigteAnfrage);
  }, [erledigteAnfrage]);

  useEffect(() => {
    vorherige.current = schritte.map((s) => s.schluessel);
    if (schritt && schritt.schluessel !== aktuell) {
      setAktuell(schritt.schluessel);
      setBesucht((alt) => new Set(alt).add(schritt.schluessel));
    }
  });

  // Nach einem Schrittwechsel den Titel fokussieren – rollt ihn auch ins Bild.
  useEffect(() => {
    if (navigiert.current) titel.current?.focus();
    navigiert.current = false;
  }, [schritt?.schluessel]);

  // Ohne sichtbare Felder meldet das schon der Konfigurator selbst.
  if (!schritt) return null;

  const letzter = index === schritte.length - 1;

  function gehe(ziel: number) {
    sprung.current = null;
    const naechster = schritte[ziel];
    if (naechster) springeZu(naechster.schluessel);
  }

  const schrittFelder = schritt.felder.flatMap((k) => felder.get(k) ?? []);
  // „Weiter“ erst, wenn kein Feld des Schritts einen Fehler hat und alle Pflichtfelder einen Wert.
  const blockiert = schrittFelder.some(
    (f) => f.zustand.fehler !== null || (f.zustand.pflicht && (werte[f.schluessel] === undefined || werte[f.schluessel] === "")),
  );

  // Eine Karten-Auswahl, die allein im Schritt steht (ohne weitere Felder und Vorschau), springt weiter.
  const deklariert = stand.schritte?.find((s) => s.schluessel === schritt.schluessel) ?? schritt;
  const springt =
    schrittFelder.length === 1 &&
    schrittFelder[0]!.darstellung === "karten" &&
    deklariert.felder.length === 1 &&
    deklariert.vorschau.length === 0;

  const bausteine = schritt.vorschau.flatMap((k) => vorschau.find((b) => b.schluessel === k) ?? []);
  const alleFelder = schritte.flatMap((s) => s.felder).flatMap((k) => felder.get(k) ?? []);

  return (
    <div className="konfi-wizard">
      <ol className="konfi-schrittleiste" aria-label="Schritte">
        {schritte.map((s, i) => {
          const istAktuell = i === index;
          return (
            <li key={s.schluessel}>
              <button
                type="button"
                className="konfi-schrittknopf"
                aria-current={istAktuell ? "step" : undefined}
                aria-label={`Schritt ${i + 1}: ${s.titel}`}
                title={s.titel}
                data-besucht={besucht.has(s.schluessel) || undefined}
                disabled={!istAktuell && !besucht.has(s.schluessel)}
                onClick={() => !istAktuell && gehe(i)}
              >
                <span className="konfi-schrittknopf__nr">{i + 1}</span>
                {istAktuell && <span className="konfi-schrittknopf__titel">{s.titel}</span>}
              </button>
            </li>
          );
        })}
      </ol>

      <section className="konfi-schritt" aria-busy={rechnet}>
        <header className="konfi-schritt__kopf">
          <p className="konfi-schritt__zaehler">
            Schritt {index + 1} von {schritte.length}
          </p>
          <h2 ref={titel} tabIndex={-1}>
            {schritt.titel}
          </h2>
          {schritt.hinweis && <p className="muted small">{schritt.hinweis}</p>}
        </header>

        {schrittFelder.length > 0 && (
          <div className="konfi-schritt__felder">
            {schrittFelder.map((feld) => (
              <FeldEingabe
                key={feld.schluessel}
                feld={feld}
                wert={werte[feld.schluessel]}
                onChange={(wert) => aendern(feld.schluessel, wert)}
                onGewaehlt={
                  springt && !letzter ? () => (sprung.current = { von: schritt.schluessel, nach: gestarteteAnfrage() }) : undefined
                }
                // Steht das Feld allein im Schritt und heißt wie er, nennt der Titel es schon.
                beschriftungVerbergen={schrittFelder.length === 1 && feld.label === schritt.titel}
              />
            ))}
          </div>
        )}

        {letzter && alleFelder.length > 0 && (
          <div className="konfi-baustein">
            <h3>Übersicht</h3>
            <dl className="konfi-uebersicht">
              {alleFelder.map((feld) => (
                <div key={feld.schluessel}>
                  <dt>{feld.label}</dt>
                  <dd>{anzeigeWert(feld, werte[feld.schluessel])}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <VorschauBausteine bausteine={bausteine} werte={werte} onChange={aendern} />

        <nav className="konfi-navigation" aria-label="Wizard">
          <button type="button" disabled={index === 0} onClick={() => gehe(index - 1)}>
            Zurück
          </button>
          {!letzter && (
            <button type="button" className="primary" disabled={blockiert} onClick={() => gehe(index + 1)}>
              Weiter
            </button>
          )}
        </nav>
      </section>
    </div>
  );
}
